/**
 * NewClinicTicketPage — open a new ticket addressed to a CLINIC.
 *
 * Route: `/clinic-tickets/new` (ProtectedRoute).
 *
 * The customer picks WHICH clinic from a dropdown of clinics they are allowed
 * to message — i.e. clinics they PAID for a service at within the last 3
 * months (GET /user/clinic-tickets/eligible-clinics, derived from successful
 * transactions). They cannot message a clinic they've never been to; the
 * backend enforces the same rule on create (403 CLINIC_NOT_ELIGIBLE), so the
 * dropdown is convenience, not the security boundary.
 *
 * `?clinicCode=` (from a deep link) preselects that clinic IF it's eligible.
 * Optional `?relatedType=&relatedId=` link the ticket to a record.
 *
 * On submit: POST /user/clinic-tickets → navigate to the new ticket.
 */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Stack,
  Typography,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Skeleton,
} from '@mui/material';
import Container from '@/components/PageSheet';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SendIcon from '@mui/icons-material/Send';
import RefreshIcon from '@mui/icons-material/Refresh';

import LanguageToggle from '@/components/LanguageToggle';
import { clinicTicketsApi } from '@/services/api';
import { selectIsRtl, selectLanguage } from '@/store/uiSlice';
import { localized } from '@/utils/localizedContent';
import { CLINIC_SUPPORT_CATEGORIES } from '@/utils/ticketCategories';

// Clinic-support topics — about the SERVICE received at the clinic.
const CATEGORIES = CLINIC_SUPPORT_CATEGORIES;

const SUBJECT_MAX = 120;
const MESSAGE_MAX = 2000;

export function NewClinicTicketPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isRtl = useSelector(selectIsRtl);
  const language = useSelector(selectLanguage);
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const relatedType = searchParams.get('relatedType');
  const relatedId = searchParams.get('relatedId');
  const prefillCode = searchParams.get('clinicCode') || '';

  const [clinicCode, setClinicCode] = useState('');
  const [clinicError, setClinicError] = useState(null);
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState('general');
  const [message, setMessage] = useState('');
  const [error, setError] = useState(null);

  // Clinics the customer is allowed to message (paid within last 3 months).
  const {
    data: clinicsData,
    isLoading: clinicsLoading,
    isError: clinicsError,
    refetch: refetchClinics,
    isFetching: clinicsFetching,
  } = useQuery({
    queryKey: ['clinicTickets', 'eligible-clinics'],
    queryFn: () => clinicTicketsApi.listEligibleClinics(),
    staleTime: 60 * 1000,
  });

  const clinics = useMemo(() => clinicsData?.data?.clinics ?? [], [clinicsData]);
  const hasClinics = clinics.length > 0;

  // Preselect: a matching deep-link code if eligible, else the only clinic.
  useEffect(() => {
    if (clinicCode || !hasClinics) return;
    if (prefillCode && clinics.some((c) => c.clinicCode === prefillCode)) {
      setClinicCode(prefillCode);
    } else if (clinics.length === 1) {
      setClinicCode(clinics[0].clinicCode);
    }
  }, [clinics, hasClinics, prefillCode, clinicCode]);

  const mutation = useMutation({
    mutationFn: () =>
      clinicTicketsApi.createTicket({
        clinicCode,
        subject,
        category,
        message,
        relatedType: relatedType || null,
        relatedId: relatedId || null,
      }),
    onSuccess: ({ data }) => {
      queryClient.invalidateQueries({ queryKey: ['clinicTickets'] });
      navigate(`/clinic-tickets/${data.ticket.id}`, { replace: true });
    },
    onError: (err) => {
      const code = err?.response?.data?.error?.code;
      if (code === 'CLINIC_NOT_ELIGIBLE' || code === 'CLINIC_NOT_FOUND') {
        setClinicError(t('clinicSupport.new.clinic.notEligible'));
      } else if (code === 'VALIDATION_ERROR') {
        setError(t('clinicSupport.new.validation'));
      } else {
        setError(t('clinicSupport.error.generic'));
      }
    },
  });

  const handleSubmit = () => {
    setError(null);
    setClinicError(null);
    if (!clinicCode) {
      setClinicError(t('clinicSupport.new.clinic.required'));
      return;
    }
    if (!subject.trim()) {
      setError(t('clinicSupport.new.subjectRequired'));
      return;
    }
    if (!message.trim()) {
      setError(t('clinicSupport.new.messageRequired'));
      return;
    }
    mutation.mutate();
  };

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/clinic-tickets');
  };

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 } }}>
      <Stack spacing={3}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton
              size="small"
              onClick={handleBack}
              aria-label={t('common.back')}
              disabled={mutation.isPending}
            >
              {isRtl ? <ArrowForwardIcon /> : <ArrowBackIcon />}
            </IconButton>
            <Typography variant="h6" component="h1" fontWeight={700}>
              {t('clinicSupport.new.title')}
            </Typography>
          </Stack>
          <LanguageToggle />
        </Stack>

        {/* Linked-record banner (optional deep link) */}
        {relatedType && relatedId && (
          <Card variant="outlined" sx={{ bgcolor: 'primary.50' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  size="small"
                  label={t(`clinicSupport.new.related.${relatedType}`, {
                    defaultValue: relatedType,
                  })}
                  variant="outlined"
                />
                <Typography variant="caption" color="text.secondary">
                  {t('clinicSupport.new.relatedHint')}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* Loading eligible clinics */}
        {clinicsLoading && (
          <Stack spacing={2}>
            <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
          </Stack>
        )}

        {/* Failed to load eligible clinics */}
        {!clinicsLoading && clinicsError && (
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={() => refetchClinics()}
                disabled={clinicsFetching}
              >
                {t('common.retry')}
              </Button>
            }
          >
            {t('clinicSupport.new.clinic.loadError')}
          </Alert>
        )}

        {/* No eligible clinics — the customer hasn't paid anywhere recently */}
        {!clinicsLoading && !clinicsError && !hasClinics && (
          <Stack spacing={2}>
            <Alert severity="info">{t('clinicSupport.new.clinic.none')}</Alert>
            <Button variant="outlined" onClick={handleBack}>
              {t('common.back')}
            </Button>
          </Stack>
        )}

        {/* Form — only when the customer has at least one clinic to message */}
        {!clinicsLoading && !clinicsError && hasClinics && (
          <Stack spacing={2}>
            <TextField
              select
              label={t('clinicSupport.new.clinic.label')}
              value={clinicCode}
              onChange={(e) => {
                setClinicCode(e.target.value);
                if (clinicError) setClinicError(null);
              }}
              disabled={mutation.isPending}
              fullWidth
              error={!!clinicError}
              helperText={clinicError ?? t('clinicSupport.new.clinic.helper')}
            >
              {clinics.map((c) => (
                <MenuItem key={c.clinicCode} value={c.clinicCode}>
                  {localized(c.clinicName, language) || c.clinicCode}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label={t('clinicSupport.new.subject')}
              value={subject}
              onChange={(e) => {
                setSubject(e.target.value);
                if (error) setError(null);
              }}
              disabled={mutation.isPending}
              fullWidth
              inputProps={{ maxLength: SUBJECT_MAX, 'aria-label': t('clinicSupport.new.subject') }}
              helperText={`${subject.length}/${SUBJECT_MAX}`}
            />

            <TextField
              select
              label={t('clinicSupport.new.category')}
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              disabled={mutation.isPending}
              fullWidth
              helperText={t('clinicSupport.new.categoryHelper')}
            >
              {CATEGORIES.map((c) => (
                <MenuItem key={c} value={c}>
                  {t(`clinicSupport.category.${c}`)}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label={t('clinicSupport.new.message')}
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
                if (error) setError(null);
              }}
              disabled={mutation.isPending}
              fullWidth
              multiline
              rows={6}
              inputProps={{ maxLength: MESSAGE_MAX, 'aria-label': t('clinicSupport.new.message') }}
              helperText={`${message.length}/${MESSAGE_MAX}`}
            />

            {error && <Alert severity="error">{error}</Alert>}

            <Stack direction="row" spacing={1.5} sx={{ pt: 1 }}>
              <Button
                variant="outlined"
                onClick={handleBack}
                disabled={mutation.isPending}
                sx={{ flex: 1 }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={mutation.isPending}
                startIcon={
                  mutation.isPending ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <SendIcon />
                  )
                }
                sx={{ flex: 2 }}
              >
                {mutation.isPending
                  ? t('clinicSupport.new.submitting')
                  : t('clinicSupport.new.submit')}
              </Button>
            </Stack>
          </Stack>
        )}
      </Stack>
    </Container>
  );
}

export default NewClinicTicketPage;
